/**
 * MTO (Made-to-Order) localStorage Cache System
 * Efficiently loads and caches metaobject translations
 * Version: 2.0.0
 */

(function() {
    'use strict';
    
    // Configuration
    const CACHE_KEY = 'mto_' + (window.Shopify?.shop?.split('.')[0] || 'unknown') + '_v2';
    const CACHE_DURATION = 24 * 60 * 60 * 1000; // 24 hours
    let DEBUG = window.MTO_DEBUG || false;
    
    // Track initialization state
    let isInitialized = false;
    const initializationCallbacks = [];
    
    /**
     * Initialize MTO cache system with provided metaobjects
     * @param {Object} options - Configuration options
     * @param {Array} [options.metaobjects] - Array of metaobject data
     * @param {boolean} [options.debug] - Enable debug logging
     */
    function init(options = {}) {
        if (isInitialized) {
            if (DEBUG) console.log('ℹ️ MTO Cache already initialized');
            return;
        }
        
        DEBUG = options.debug !== undefined ? options.debug : DEBUG;
        window.MTO_DEBUG = DEBUG;
        
        // Ensure theme object exists
        window.theme = window.theme || {};
        
        // Initialize MTO lookup structure
        if (!theme.mtoLookup) {
            theme.mtoLookup = { 
                byVariant: {},
                metaobjects: []
            };
        }
        
        // Process provided metaobjects if any
        if (options.metaobjects?.length > 0) {
            if (DEBUG) console.log('📦 Initializing MTO cache with', options.metaobjects.length, 'metaobjects');
            processMetaobjects(options.metaobjects);
        } else if (loadFromCache()) {
            if (DEBUG) console.log('✅ MTO cache loaded from localStorage');
        } else {
            if (DEBUG) console.warn('⚠️ No metaobjects provided and no cache available');
        }
        
        isInitialized = true;
        notifyInitializationComplete();
    }
    
    /**
     * Process metaobjects and build lookup tables
     * @param {Array} metaobjects - Array of metaobject data
     */
    function processMetaobjects(metaobjects) {
        if (!metaobjects || !Array.isArray(metaobjects)) {
            if (DEBUG) console.error('❌ Invalid metaobjects data');
            return;
        }
        
        const variantLookup = {};
        const metaobjectList = [];
        let totalVariants = 0;
        
        metaobjects.forEach((mto, index) => {
            if (!mto) return;
            
            const identifier = mto.product_identifier || `metaobject-${index}`;
            const translations = {
                en: mto.text_in_english || '',
                cs: mto.text_in_czech || mto.text_in_english || '',
                de: mto.text_in_german || mto.text_in_english || '',
                fr: mto.text_in_french || mto.text_in_english || '',
                source: identifier,
                updatedAt: Date.now()
            };
            
            const variantIds = [];
            let variantCount = 0;
            
            // Process variants if available
            if (mto.product_list?.value?.length) {
                mto.product_list.value.forEach(variant => {
                    if (!variant) return;
                    
                    // Handle different ID formats
                    const idStr = String(variant.id || variant.numeric_id || '');
                    const idNum = idStr.replace(/\D/g, ''); // Extract numbers only
                    
                    // Map all possible ID formats to the same translations
                    [idStr, idNum, `gid://shopify/ProductVariant/${idNum}`, `gid://shopify/ProductVariant/${idStr}`]
                        .filter(Boolean)
                        .forEach(id => {
                            if (!variantLookup[id]) {
                                variantLookup[id] = translations;
                                variantIds.push(id);
                                variantCount++;
                                totalVariants++;
                                
                                // Debug logging for first few variants
                                if (DEBUG && totalVariants <= 3) {
                                    console.log('🔍 Sample variant mapping:', {
                                        metaobject: identifier,
                                        variantId: id,
                                        hasTranslations: true
                                    });
                                }
                            }
                        });
                });
            }
            
            // Store metaobject info
            metaobjectList.push({
                id: mto.id || `mto-${index}`,
                identifier: identifier,
                variantCount: variantCount,
                firstFewVariants: variantIds.slice(0, 2)
            });
            
            if (DEBUG && variantCount > 0) {
                console.log(`📦 Processed ${variantCount} variants for ${identifier}`);
            }
        });
        
        // Update global state
        theme.mtoLookup.byVariant = variantLookup;
        theme.mtoLookup.metaobjects = metaobjectList;
        
        // Cache the results
        saveToCache(variantLookup);
        
        if (DEBUG) {
            console.log(`✅ Processed ${metaobjectList.length} metaobjects with ${totalVariants} variant mappings`);
            
            // Show summary statistics
            console.log('📈 Summary:', {
                'Total Variant Mappings': Object.keys(variantLookup).length,
                'Total Metaobjects': metaobjectList.length,
                'Avg Variants per Metaobject': metaobjectList.length > 0 ? 
                    (totalVariants / metaobjectList.length).toFixed(1) : 0,
                'Cache Size': `${JSON.stringify(variantLookup).length} bytes`
            });
            
            // Show sample of metaobjects
            if (metaobjectList.length > 0) {
                console.log('🔍 Sample Metaobjects:', metaobjectList.slice(0, 3));
            }
            
            // Check for any potential issues
            const emptyMetaobjects = metaobjectList.filter(m => m.variantCount === 0);
            if (emptyMetaobjects.length > 0) {
                console.warn('⚠️ Found', emptyMetaobjects.length, 'metaobjects with no variants');
            }
        }
        
        // Notify that initialization is complete
        document.dispatchEvent(new CustomEvent('mtoCacheReady'));
    }
    
    /**
     * Load MTO data from localStorage cache
     * @returns {boolean} true if cache was valid and loaded
     */
    function loadFromCache() {
        try {
            const cached = JSON.parse(localStorage.getItem(CACHE_KEY));
            
            if (!cached || !cached.data || !cached.timestamp) {
                if (DEBUG) console.log('ℹ️ No valid cache found');
                return false;
            }
            
            // Check if cache is still valid
            const now = Date.now();
            if (now - cached.timestamp > CACHE_DURATION) {
                if (DEBUG) console.log('📅 MTO cache expired');
                localStorage.removeItem(CACHE_KEY);
                return false;
            }
            
            // Check cache version
            if (cached.version !== '2.0') {
                if (DEBUG) console.log('🔄 Cache version mismatch, rebuilding...');
                localStorage.removeItem(CACHE_KEY);
                return false;
            }
            
            // Load cached data into theme lookup
            theme.mtoLookup.byVariant = cached.data || {};
            
            if (DEBUG) {
                console.log(`✅ Loaded ${Object.keys(cached.data).length} variant mappings from cache`);
            }
            
            return true;
            
        } catch (error) {
            if (DEBUG) console.error('❌ MTO cache load error:', error);
            localStorage.removeItem(CACHE_KEY);
            return false;
        }
    }
    
    /**
     * Save MTO data to localStorage cache
     * @param {Object} data - The data to cache
     */
    function saveToCache(data) {
        try {
            const cacheData = {
                data: data,
                timestamp: Date.now(),
                version: '2.0',
                shop: window.Shopify?.shop
            };
            
            localStorage.setItem(CACHE_KEY, JSON.stringify(cacheData));
            
            if (DEBUG) console.log('💾 MTO data cached successfully');
            
        } catch (error) {
            if (DEBUG) console.error('❌ MTO cache save error:', error);
        }
    }
    
    /**
     * Get MTO text for a variant
     * @param {string} locale - Language code (en, cs, de, fr)
     * @param {string|number} variantId - Variant ID to look up
     * @returns {string} Translated text or fallback
     */
    function getText(locale = 'en', variantId) {
        if (!variantId && variantId !== 0) {
            if (DEBUG) console.warn('⚠️ getText called without variantId');
            return getFallbackText(locale);
        }
        
        const variantIdStr = String(variantId);
        const variantKey = findVariantKey(variantIdStr);
        
        if (!variantKey) {
            if (DEBUG) console.log(`🔍 Variant ${variantIdStr} not found in MTO lookup`);
            return getFallbackText(locale);
        }
        
        const translations = theme.mtoLookup.byVariant[variantKey];
        const lang = translations[locale] ? locale : 'en';
        const text = translations[lang] || translations.en;
        
        if (DEBUG) {
            console.log(`🔍 MTO Lookup:`, {
                variantId: variantIdStr,
                foundKey: variantKey,
                locale: locale,
                usedLocale: lang,
                source: translations.source || 'unknown',
                text: text || 'No text available'
            });
        }
        
        return text || getFallbackText(locale);
    }
    
    /**
     * Find the correct variant key in the lookup table
     * @param {string} variantId - Variant ID to find
     * @returns {string|undefined} The matching key or undefined
     */
    function findVariantKey(variantId) {
        if (!variantId) return undefined;
        
        const strId = String(variantId);
        const numId = strId.replace(/\D/g, '');
        const gid = strId.startsWith('gid://') ? strId : `gid://shopify/ProductVariant/${numId}`;
        
        // Try different key formats
        return [
            strId,                              // Original ID
            numId,                             // Numeric ID only
            gid,                               // Full GID
            `gid://shopify/ProductVariant/${strId}`,  // GID with original ID
            `gid://shopify/ProductVariant/${numId}`   // GID with numeric ID
        ].find(key => key && theme.mtoLookup.byVariant[key]);
    }
    
    /**
     * Get fallback text when no MTO text is found
     * @param {string} locale - Language code
     * @returns {string} Fallback text
     */
    function getFallbackText(locale) {
        const fallbacks = {
            en: theme.strings?.waitingForStock || 'Currently unavailable',
            cs: 'Momentálně nedostupné',
            de: 'Derzeit nicht verfügbar',
            fr: 'Actuellement indisponible'
        };
        
        return fallbacks[locale] || fallbacks.en;
    }
    
    /**
     * Clear the MTO cache
     */
    function clearCache() {
        try {
            localStorage.removeItem(CACHE_KEY);
            if (theme.mtoLookup) {
                theme.mtoLookup.byVariant = {};
                theme.mtoLookup.metaobjects = [];
            }
            isInitialized = false;
            if (DEBUG) console.log('🧹 MTO cache cleared');
            return true;
        } catch (error) {
            if (DEBUG) console.error('❌ Failed to clear MTO cache:', error);
            return false;
        }
    }
    
    /**
     * Debug function to check a specific variant
     * @param {string|number} variantId - Variant ID to check
     */
    function debugVariant(variantId) {
        if (!variantId) {
            console.log('❌ No variant ID provided');
            return;
        }
        
        const variantIdStr = String(variantId);
        const variantKey = findVariantKey(variantIdStr);
        
        if (!variantKey) {
            console.log(`❌ Variant ${variantIdStr} not found in MTO lookup`);
            console.log('Available variant keys:', Object.keys(theme.mtoLookup.byVariant || {}).slice(0, 5), '...');
            return;
        }
        
        const data = theme.mtoLookup.byVariant[variantKey];
        console.log(`🔍 Debug variant ${variantIdStr}:`, {
            foundAs: variantKey,
            source: data.source || 'unknown',
            translations: {
                en: data.en || 'N/A',
                cs: data.cs || 'N/A',
                de: data.de || 'N/A',
                fr: data.fr || 'N/A'
            },
            rawData: data
        });
    }
    
    /**
     * Register a callback to be called when initialization is complete
     * @param {Function} callback - Function to call
     */
    function onReady(callback) {
        if (typeof callback !== 'function') return;
        
        if (isInitialized) {
            callback();
        } else {
            initializationCallbacks.push(callback);
        }
    }
    
    /**
     * Notify all registered callbacks that initialization is complete
     */
    function notifyInitializationComplete() {
        while (initializationCallbacks.length > 0) {
            const callback = initializationCallbacks.shift();
            try {
                callback();
            } catch (error) {
                console.error('MTO callback error:', error);
            }
        }
    }
    
    // Auto-initialize if metaobjects are already available
    if (window.mtoMetaobjectsCache && window.mtoMetaobjectsCache.length > 0) {
        init({ metaobjects: window.mtoMetaobjectsCache });
    }
    
    // Expose public API
    window.MtoCache = {
        // Core functions
        init,
        getText,
        clearCache,
        debugVariant,
        onReady,
        
        // Version info
        version: '2.0.0',
        
        // Debug mode
        set debug(enabled) {
            DEBUG = !!enabled;
            window.MTO_DEBUG = DEBUG;
        },
        get debug() {
            return DEBUG;
        }
    };
    
    // For backward compatibility
    if (typeof module !== 'undefined' && module.exports) {
        module.exports = window.MtoCache;
    }
    
    // Auto-initialize on DOM ready if not already initialized
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => {
            if (!isInitialized) init();
        });
    } else if (!isInitialized) {
        init();
    }
    
})();